import React, { useState, useMemo, useEffect } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import {
  X as XIcon, Loader2, ShieldAlert, MapPin, AlertTriangle, RefreshCw,
  Trash2, FileText, School, Bus, Building2, Wrench, Warehouse, Accessibility,
  ArrowRight, CheckCircle2,
} from "lucide-react";

/**
 * Phase C — Quality drawer on top of the Arrêts page.
 *
 * Buckets shown:
 *   missing_type, missing_address, missing_coords, inactive,
 *   unused, school_candidates, duplicates
 *
 * Actions wired in:
 *   - "Reclassifier auto" → POST /api/logibus/poi/reclassify-all
 *   - "Export CSV"       → builds a CSV blob client-side
 *   - "Modifier"         → opens the POIEditorModal via onEditStop()
 *   - "Voir sur la carte"→ centers the map via onFocusStop()
 */
const BUCKET_META = {
  missing_type:       { label: "Sans type opérationnel", Icon: AlertTriangle, tone: "amber",   hint: "POIs encore tagués 'stop' faute de mot-clé reconnu." },
  missing_address:    { label: "Sans adresse",           Icon: MapPin,        tone: "amber",   hint: "Ajoutez une adresse pour faciliter la recherche." },
  missing_coords:     { label: "Sans coordonnées",       Icon: AlertTriangle, tone: "red",     hint: "Impossible de placer sur la carte — corrigez les lat/lng." },
  inactive:           { label: "Désactivés",             Icon: AlertTriangle, tone: "navy",    hint: "Cachés des recherches & créations de tournée." },
  unused:             { label: "Non utilisés",           Icon: AlertTriangle, tone: "muted",   hint: "POIs jamais référencés dans une tournée." },
  school_candidates:  { label: "Écoles potentielles",    Icon: School,        tone: "amber",   hint: "Nom suggère une école — re-tagguez en 'School'." },
  duplicates:         { label: "Doublons potentiels",    Icon: AlertTriangle, tone: "red",     hint: "Deux POIs à <110 m avec un nom similaire." },
};

const TONE_CLASSES = {
  amber:  "border-amber-700  bg-amber-50  text-amber-800",
  red:    "border-red-700    bg-red-50    text-red-800",
  navy:   "border-zinc-700   bg-zinc-50   text-zinc-700",
  muted:  "border-zinc-300   bg-white     text-zinc-600",
};

export default function POIQualityDrawer({ onClose, onEditStop, onFocusStop }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [reclassifying, setReclassifying] = useState(false);
  const [activeBucket, setActiveBucket] = useState("missing_type");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/logibus/poi/quality");
      setData(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const reclassify = async () => {
    if (!confirm("Lancer la reclassification automatique sur tous les POIs ?\nLes types saisis manuellement ne seront PAS modifiés.")) return;
    setReclassifying(true);
    try {
      const { data: r } = await api.post("/logibus/poi/reclassify-all");
      toast.success(`Reclassification terminée — ${r.total} POI mis à jour.`);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message);
    } finally { setReclassifying(false); }
  };

  const exportCSV = () => {
    if (!data) return;
    const rows = [];
    rows.push(["bucket", "stop_id", "stop_name", "type", "city", "source", "lat", "lon"].join(","));
    Object.entries(data.buckets || {}).forEach(([bucket, items]) => {
      if (bucket === "duplicates") {
        items.forEach((d) => {
          rows.push([bucket, d.a.stop_id, csv(d.a.stop_name), d.a.type || "", d.a.city || "", d.a.source || "", "", ""].join(","));
          rows.push([bucket, d.b.stop_id, csv(d.b.stop_name), d.b.type || "", d.b.city || "", d.b.source || "", "", ""].join(","));
        });
      } else {
        items.forEach((s) => {
          rows.push([bucket, s.stop_id, csv(s.stop_name), s.type || "", s.city || "", s.source || "", s.stop_lat ?? "", s.stop_lon ?? ""].join(","));
        });
      }
    });
    const blob = new Blob([rows.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `poi-quality-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("Export CSV téléchargé");
  };

  const buckets = data?.buckets || {};
  const totals = data?.totals || {};
  const items = activeBucket === "duplicates" ? (buckets.duplicates || []) : (buckets[activeBucket] || []);

  return (
    <div
      className="fixed inset-0 z-[900] bg-black/40"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      data-testid="poi-quality-drawer"
    >
      <aside className="ml-auto h-full bg-white border-l border-zinc-900 w-full max-w-2xl flex flex-col shadow-[-6px_0_0_0_#0A0A0B]">
        <header className="px-5 py-4 border-b border-zinc-900 bg-zinc-900 text-white flex items-center justify-between flex-shrink-0">
          <div>
            <div className="text-[10px] font-mono tracking-[0.3em] uppercase text-zinc-400">Phase C · Outil d'audit</div>
            <h2 className="font-display font-black text-2xl tracking-tighter mt-0.5">Qualité des POI</h2>
            {data && (
              <div className="text-[11px] font-mono text-zinc-300 mt-1">
                {totals.total_pois ?? "?"} POIs au total · {Object.entries(totals).filter(([k, v]) => k !== "total_pois" && v > 0).length} catégories d'anomalies
              </div>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={exportCSV}
              disabled={!data}
              data-testid="quality-export-csv"
              className="border border-zinc-500 bg-white text-zinc-900 px-2 py-1.5 text-[10px] font-bold uppercase tracking-widest hover:bg-[#FFC60A] disabled:opacity-50 inline-flex items-center gap-1"
            >
              <FileText size={11} /> Export CSV
            </button>
            <button
              onClick={reclassify}
              disabled={reclassifying}
              data-testid="quality-reclassify"
              className="border border-zinc-500 bg-[#FFC60A] text-zinc-900 px-2 py-1.5 text-[10px] font-bold uppercase tracking-widest hover:bg-[#ffd43a] disabled:opacity-50 inline-flex items-center gap-1"
            >
              {reclassifying ? <Loader2 size={11} className="animate-spin" /> : <RefreshCw size={11} />}
              Reclassifier auto
            </button>
            <button onClick={onClose} data-testid="quality-close" className="text-zinc-400 hover:text-white p-1.5">
              <XIcon size={16} />
            </button>
          </div>
        </header>

        {loading && !data && (
          <div className="p-8 text-center text-sm font-mono text-zinc-500">
            <Loader2 size={16} className="inline animate-spin mr-1" /> Calcul des anomalies…
          </div>
        )}

        {data && (
          <>
            {/* Bucket selector grid */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-0 border-b border-zinc-900 flex-shrink-0">
              {Object.entries(BUCKET_META).map(([key, meta]) => {
                const count = (totals[key] ?? 0);
                const Icon = meta.Icon;
                const active = activeBucket === key;
                return (
                  <button
                    key={key}
                    onClick={() => setActiveBucket(key)}
                    data-testid={`quality-bucket-${key}`}
                    className={`px-3 py-2.5 border-r border-zinc-900 last:border-r-0 text-left transition-colors ${
                      active ? "bg-[#FFC60A]" : count > 0 ? "bg-white hover:bg-zinc-50" : "bg-zinc-100 text-zinc-400"
                    }`}
                  >
                    <div className="inline-flex items-center gap-1.5 mb-0.5">
                      <Icon size={11} />
                      <span className="text-[9px] font-mono uppercase tracking-[0.2em]">{meta.label}</span>
                    </div>
                    <div className="font-display font-black text-xl tracking-tighter">{count}</div>
                  </button>
                );
              })}
            </div>

            {/* Active bucket details */}
            <div className="p-4 border-b border-zinc-200 flex-shrink-0">
              <div className="text-[10px] font-mono tracking-widest uppercase text-zinc-500 mb-1">
                {BUCKET_META[activeBucket]?.label} · {items.length} résultat{items.length > 1 ? "s" : ""}
              </div>
              <p className="text-xs text-zinc-600">{BUCKET_META[activeBucket]?.hint}</p>
            </div>

            {/* List */}
            <div className="flex-1 overflow-y-auto" data-testid={`quality-list-${activeBucket}`}>
              {items.length === 0 ? (
                <div className="p-10 text-center text-xs text-zinc-400">
                  <CheckCircle2 size={32} className="mx-auto text-emerald-400 mb-2" />
                  Aucun problème dans cette catégorie. 👍
                </div>
              ) : activeBucket === "duplicates" ? (
                <ul className="divide-y divide-zinc-100">
                  {items.map((d, i) => (
                    <li key={i} className="p-3 hover:bg-zinc-50" data-testid={`quality-dup-${i}`}>
                      <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-1">Doublon n°{i + 1}</div>
                      <DuplicateRow stop={d.a} onEdit={() => onEditStop?.(d.a)} onFocus={() => onFocusStop?.(d.a.stop_id)} />
                      <div className="text-center text-[9px] font-mono text-zinc-400 my-0.5">≈</div>
                      <DuplicateRow stop={d.b} onEdit={() => onEditStop?.(d.b)} onFocus={() => onFocusStop?.(d.b.stop_id)} />
                    </li>
                  ))}
                </ul>
              ) : (
                <ul className="divide-y divide-zinc-100">
                  {items.map((s) => (
                    <li key={s.stop_id} className="p-3 hover:bg-zinc-50 flex items-center gap-2" data-testid={`quality-item-${s.stop_id}`}>
                      <TypeIcon type={s.type} />
                      <div className="flex-1 min-w-0">
                        <div className="font-bold text-sm truncate">{s.stop_name || s.stop_id}</div>
                        <div className="text-[10px] font-mono text-zinc-500 flex flex-wrap gap-x-2 mt-0.5">
                          {s.type && <span className="uppercase">{s.type}</span>}
                          {s.city && <span>· {s.city}</span>}
                          {s.source && <span>· {s.source}</span>}
                          {s.address && <span className="truncate">· {s.address}</span>}
                        </div>
                      </div>
                      <button
                        onClick={() => onFocusStop?.(s.stop_id)}
                        title="Voir sur la carte"
                        data-testid={`quality-focus-${s.stop_id}`}
                        className="px-2 py-1 border border-zinc-300 hover:bg-zinc-900 hover:text-white text-[10px] font-mono uppercase tracking-widest inline-flex items-center gap-1"
                      >
                        <ArrowRight size={10} /> Carte
                      </button>
                      <button
                        onClick={() => onEditStop?.(s)}
                        title="Modifier ce POI"
                        data-testid={`quality-edit-${s.stop_id}`}
                        className="px-2 py-1 border border-zinc-900 bg-[#FFC60A] hover:bg-[#ffd43a] text-[10px] font-bold uppercase tracking-widest"
                      >
                        Modifier
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </>
        )}
      </aside>
    </div>
  );
}

function DuplicateRow({ stop, onEdit, onFocus }) {
  return (
    <div className="flex items-center gap-2 p-1.5 bg-white border border-zinc-200">
      <TypeIcon type={stop.type} small />
      <div className="flex-1 min-w-0">
        <div className="font-semibold text-xs truncate">{stop.stop_name}</div>
        <div className="text-[10px] font-mono text-zinc-500 truncate">
          {stop.stop_id} · {stop.city || "—"} · {stop.source || "—"}
        </div>
      </div>
      <button onClick={onFocus} className="p-1 border border-zinc-300 hover:bg-zinc-900 hover:text-white" title="Voir">
        <ArrowRight size={10} />
      </button>
      <button onClick={onEdit} className="p-1 border border-zinc-900 bg-[#FFC60A] hover:bg-[#ffd43a]" title="Modifier">
        <Trash2 size={10} />
      </button>
    </div>
  );
}

const TYPE_ICONS = {
  school: { Icon: School, color: "#F59E0B" },
  stop:   { Icon: Bus, color: "#3B82F6" },
  pmr:    { Icon: Accessibility, color: "#0EA5E9" },
  depot:  { Icon: Warehouse, color: "#71717A" },
  workshop: { Icon: Wrench, color: "#71717A" },
  client: { Icon: Building2, color: "#10B981" },
  other:  { Icon: MapPin, color: "#A1A1AA" },
};

function TypeIcon({ type, small = false }) {
  const m = TYPE_ICONS[type] || TYPE_ICONS.other;
  const Icon = m.Icon;
  return (
    <span
      className={`${small ? "w-5 h-5" : "w-6 h-6"} flex items-center justify-center border border-zinc-900 flex-shrink-0`}
      style={{ background: m.color, color: "white" }}
    >
      <Icon size={small ? 10 : 12} />
    </span>
  );
}

function csv(s) {
  if (s == null) return "";
  const v = String(s);
  return /[",\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v;
}
